import { Link, useLocation } from "@tanstack/react-router";
import { Fuel, Calendar, Gauge, CalendarCheck, Car as CarIcon, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useMemo, useRef, lazy, Suspense } from "react";
import { encodeListingFrom, LISTING_FROM_PARAM } from "@/lib/listing-back-link";
import { resolveCarSlug } from "@/lib/car-slug";
import { supabaseImageUrl } from "@/lib/supabase-image";
import { CarPicture } from "@/components/CarPicture";
import type { CarManifest } from "@/lib/car-image-manifest";

// Lazy: only loaded when the user opens the dialog/drawer. Saves ~150 KiB on
// the initial listing/home bundle (drawer pulls in Sheet+Accordion+ScrollArea).
const TestDriveDialog = lazy(() => import("@/components/TestDriveDialog").then((m) => ({ default: m.TestDriveDialog })));
const CarQuickViewDrawer = lazy(() => import("@/components/CarQuickViewDrawer").then((m) => ({ default: m.CarQuickViewDrawer })));

interface CarCardProps {
  id: string;
  brand: string;
  model: string;
  variant?: string | null;
  year: number;
  mileage: number;
  price: number;
  fuel_type: string | null;
  transmission?: string | null;
  color?: string | null;
  body_type?: string | null;
  doors?: number | null;
  seats?: number | null;
  engine_size?: string | null;
  horsepower?: number | null;
  description?: string | null;
  images: string[] | null;
  status: string;
  is_new?: boolean | null;
  is_popular?: boolean | null;
  is_leasing?: boolean | null;
  leasing_price?: number | null;
  seo_slug?: string | null;
  is_demo?: boolean | null;
  /**
   * Per-image manifest entries (keyed by `cars.images[i]` URL) for cars
   * uploaded via the new admin pipeline. Optional — when missing, the
   * card falls back to the legacy single-URL Supabase image-transform.
   */
  manifest?: CarManifest;
}

function formatPrice(price: number) {
  return new Intl.NumberFormat("da-DK").format(price);
}

function fuelLabel(fuel: string | null) {
  if (!fuel) return "-";
  const map: Record<string, string> = {
    benzin: "Benzin", diesel: "Diesel", el: "El", hybrid: "Hybrid", plugin_hybrid: "Plugin Hybrid",
  };
  return map[fuel] || fuel;
}

// NOTE: legacy `thumbs/<file>.webp` URLs (generated at upload time) are still
// honored — supabaseImageUrl() short-circuits them so we never re-transform
// an already-small thumbnail.

export function CarCard(props: CarCardProps) {
  const { id, brand, model, variant, year, mileage, price, fuel_type, images, status, is_new, is_popular, is_leasing, leasing_price, seo_slug, is_demo, manifest } = props;
  const slug = resolveCarSlug({ seo_slug, id });
  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  // Cap the in-card slider at the first 6 photos. Listing density matters
  // more than completeness here — users open the detail page for the rest.
  const galleryImages = useMemo(
    () => (images ?? []).filter(Boolean).slice(0, 6),
    [images],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasGallery = galleryImages.length > 1 && !imgFailed;
  const hasImage = galleryImages.length > 0 && !imgFailed;
  const activeImage = galleryImages[currentIndex] ?? "";
  // Look up the per-image manifest entry for the active photo. When
  // present, <CarPicture> emits a real <picture> with AVIF/WebP/JPG
  // sources at 960/1280/1920. When absent (legacy uploads, Bilinfo CDN),
  // it falls back to the existing Supabase image-transform 1x/2x srcset.
  const manifestEntry = hasImage ? manifest?.[activeImage] : undefined;
  // 24px-wide preview as the LQIP placeholder — small enough to inline-decode
  // before the high-res srcset arrives, big enough to show recognizable colors.
  const placeholder = hasImage ? supabaseImageUrl(activeImage, { width: 24, quality: 30 }) : "";
  const [testDriveOpen, setTestDriveOpen] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const carLabel = `${brand} ${model}${variant ? " " + variant : ""} (${year})`;

  // Swipe support — track the touch start X on the image area only.
  const touchStartX = useRef<number | null>(null);

  const goTo = (next: number) => {
    if (galleryImages.length === 0) return;
    const total = galleryImages.length;
    const wrapped = ((next % total) + total) % total;
    setCurrentIndex(wrapped);
    setImgLoaded(false);
  };

  // Capture the listing's current filter/sort state (q, brand, sort, …) so
  // the detail page — and the drawer's "Se fuld side" link — can ferry it
  // back via "Tilbage til oversigt". useLocation re-renders on URL changes,
  // so the encoded token always reflects the live filters.
  const location = useLocation();
  const fromToken = useMemo(
    () => encodeListingFrom(location.searchStr),
    [location.searchStr],
  );
  const detailSearch = fromToken ? { [LISTING_FROM_PARAM]: fromToken } : undefined;

  return (
    <>
      <div className="group block overflow-hidden rounded-xl bg-card shadow-sm ring-1 ring-border transition-all duration-300 hover:shadow-xl hover:ring-accent/30 hover:-translate-y-1">
        {/* aspect-[4/3] reserves exact space — prevents CLS when the image
            loads. bg-muted is the instant placeholder color; the 24px LQIP
            from Supabase image transform is painted as a blurred background
            so users see a rough preview immediately. The hi-res srcset
            variant fades in on load. */}
        <div
          className="relative aspect-[4/3] overflow-hidden bg-muted"
          style={
            hasImage && !imgLoaded
              ? {
                  backgroundImage: `url(${placeholder})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "blur(8px)",
                }
              : undefined
          }
          onTouchStart={(e) => {
            if (!hasGallery) return;
            touchStartX.current = e.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={(e) => {
            if (!hasGallery || touchStartX.current === null) return;
            const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
            const dx = endX - touchStartX.current;
            touchStartX.current = null;
            if (Math.abs(dx) > 40) {
              goTo(dx < 0 ? currentIndex + 1 : currentIndex - 1);
            }
          }}
        >
          {hasImage ? (
            <CarPicture
              key={activeImage}
              src={activeImage}
              manifest={manifestEntry}
              alt={`${brand} ${model}${galleryImages.length > 1 ? ` – billede ${currentIndex + 1} af ${galleryImages.length}` : ""}`}
              width={400}
              height={300}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 400px"
              fallbackBaseWidth={400}
              fallbackQuality={75}
              priority={currentIndex === 0}
              className={`h-full w-full object-contain transition-all duration-500 group-hover:scale-105 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
              pictureClassName="block h-full w-full"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div className="relative flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-muted via-muted to-muted/60">
              <div
                aria-hidden="true"
                className="absolute inset-0 text-accent opacity-[0.08]"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 1px, transparent 12px)",
                }}
              />
              <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-background/80 ring-1 ring-border">
                <CarIcon size={26} className="text-accent" strokeWidth={1.75} />
              </div>
              <div className="relative z-10 px-4 text-center">
                <p className="text-sm font-semibold text-foreground">{brand} {model}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Foto følger snart</p>
              </div>
            </div>
          )}

          {/* Click-through overlay → detail page. Sits BELOW the slider
              controls (z-10) so prev/next/dot clicks aren't swallowed by
              the link, but ABOVE the image so the whole frame is tappable. */}
          <Link
            to="/biler-til-salg/$slug"
            params={{ slug }}
            search={detailSearch}
            className="absolute inset-0 z-10"
            aria-label={carLabel}
          />

          {/* Slider controls — only when 2+ photos. Hidden until hover on
              desktop to keep the card visually calm; always visible on
              touch devices via the `group-hover:opacity-100` + `sm:` gate. */}
          {hasGallery && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goTo(currentIndex - 1);
                }}
                aria-label="Forrige billede"
                className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/80 p-1.5 text-foreground opacity-0 shadow-md backdrop-blur transition-opacity hover:bg-background group-hover:opacity-100 focus-visible:opacity-100"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goTo(currentIndex + 1);
                }}
                aria-label="Næste billede"
                className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full bg-background/80 p-1.5 text-foreground opacity-0 shadow-md backdrop-blur transition-opacity hover:bg-background group-hover:opacity-100 focus-visible:opacity-100"
              >
                <ChevronRight size={18} />
              </button>
              {/* Dot indicators — clickable jump points. Positioned above
                  the price gradient so they stay legible. */}
              <div className="absolute bottom-12 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
                {galleryImages.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      goTo(i);
                    }}
                    aria-label={`Gå til billede ${i + 1}`}
                    aria-current={i === currentIndex}
                    className={`h-1.5 rounded-full transition-all ${
                      i === currentIndex
                        ? "w-4 bg-white"
                        : "w-1.5 bg-white/60 hover:bg-white/80"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          <div className="pointer-events-none absolute top-3 left-3 z-20 flex gap-2">
            {is_demo && <span className="rounded-md bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">Demo</span>}
            {is_new && <span className="rounded-md bg-green-500 px-2 py-0.5 text-xs font-semibold text-white">Nyhed</span>}
            {is_popular && <span className="rounded-md bg-accent px-2 py-0.5 text-xs font-semibold text-accent-foreground">Populær</span>}
            {is_leasing && <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">Leasing</span>}
            {status === "sold" && <span className="rounded-md bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">Solgt</span>}
          </div>
          {/* Price overlay */}
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-10">
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white">{formatPrice(price)} kr</span>
              {is_leasing && leasing_price != null && leasing_price > 0 && (
                <span className="text-xs font-medium text-white/90">Leasing fra {formatPrice(leasing_price)} kr/md</span>
              )}
            </div>
          </div>
        </div>

        <Link to="/biler-til-salg/$slug" params={{ slug }} search={detailSearch} className="block">
          <div className="p-4 pb-0">
            <h3 className="text-base font-bold text-card-foreground">
              {brand} {model}
            </h3>
            {variant && <p className="mt-0.5 text-sm text-muted-foreground">{variant}</p>}

            <div className="mt-3 flex items-center gap-3 border-t border-border pt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Gauge size={13} /> {formatPrice(mileage)} km</span>
              <span className="flex items-center gap-1"><Calendar size={13} /> {year}</span>
              <span className="flex items-center gap-1"><Fuel size={13} /> {fuelLabel(fuel_type)}</span>
            </div>
          </div>
        </Link>

        <div className="flex gap-2 p-4 pt-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setQuickViewOpen(true);
            }}
            className="flex-1 rounded-lg bg-accent py-2 text-center text-sm font-semibold text-accent-foreground transition-colors hover:brightness-110 inline-flex items-center justify-center gap-1.5"
            title="Hurtig visning"
            aria-label="Hurtig visning"
          >
            <Eye size={15} /> Hurtig visning
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTestDriveOpen(true);
            }}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:text-accent"
            title="Book prøvetur"
            aria-label="Book prøvetur"
          >
            <CalendarCheck size={15} />
            <span className="hidden sm:inline">Prøvetur</span>
          </button>
        </div>
      </div>

      {testDriveOpen && (
        <Suspense fallback={null}>
          <TestDriveDialog
            open={testDriveOpen}
            onOpenChange={setTestDriveOpen}
            carId={id}
            carLabel={carLabel}
          />
        </Suspense>
      )}

      {quickViewOpen && (
        <Suspense fallback={null}>
          <CarQuickViewDrawer
            open={quickViewOpen}
            onOpenChange={setQuickViewOpen}
            car={props}
            fromToken={fromToken}
          />
        </Suspense>
      )}
    </>
  );
}
